<template>
  <v-dialog v-model="dialogVisible" max-width="900px" scrollable persistent>
    <v-card class="sensor-history-dialog">
      <v-card-title class="primary white--text py-3">
        <v-icon dark class="mr-2">history</v-icon>
        <div>
          <div class="text-h6">{{ sensorName }}</div>
          <div class="text-caption">{{ areaName }}</div>
        </div>
        <v-spacer />
        <v-btn icon dark @click="closeDialog">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-select
              v-model="dateRange"
              :items="dateRangeOptions"
              label="Date Range"
              outlined
              dense
              prepend-inner-icon="date_range"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="searchText"
              label="Search events..."
              outlined
              dense
              clearable
              prepend-inner-icon="search"
              hide-details
            />
          </v-col>
        </v-row>

        <v-card v-if="loadingUptime" class="mb-4 elevation-2">
          <v-card-text class="text-center">
            <v-progress-circular indeterminate color="primary" size="24" />
            <span class="ml-2">Loading statistics...</span>
          </v-card-text>
        </v-card>

        <v-card v-else-if="uptimeStats" class="mb-4 elevation-3 glass-stats">
          <v-card-title class="pb-2">
            <v-icon class="mr-2" color="primary">analytics</v-icon>
            Uptime Statistics
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" class="mb-3">
                <div class="text-center">
                  <div class="text-h2 primary--text font-weight-bold">
                    {{ uptimeStats.uptime_percentage }}%
                  </div>
                  <div class="text-caption text--secondary">Overall Uptime</div>
                  <v-progress-linear
                    :value="parseFloat(uptimeStats.uptime_percentage)"
                    :color="getUptimeColor(uptimeStats.uptime_percentage)"
                    height="12"
                    rounded
                    class="mt-2"
                  />
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined class="text-center pa-2">
                  <v-icon color="success" size="32">check_circle</v-icon>
                  <div class="text-h6 mt-1">{{ formatDuration(uptimeStats.online_seconds) }}</div>
                  <div class="text-caption text--secondary">Online Time</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined class="text-center pa-2">
                  <v-icon color="error" size="32">cancel</v-icon>
                  <div class="text-h6 mt-1">{{ formatDuration(uptimeStats.offline_seconds) }}</div>
                  <div class="text-caption text--secondary">Offline Time</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined class="text-center pa-2">
                  <v-icon color="info" size="32">sync</v-icon>
                  <div class="text-h6 mt-1">{{ uptimeStats.connection_events || 0 }}</div>
                  <div class="text-caption text--secondary">Total Events</div>
                </v-card>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-card outlined class="text-center pa-2">
                  <v-icon color="warning" size="32">access_time</v-icon>
                  <div class="text-h6 mt-1">{{ averageSessionDuration }}</div>
                  <div class="text-caption text--secondary">Avg Session</div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="elevation-2">
          <v-card-title class="pb-2">
            <v-icon class="mr-2" color="primary">timeline</v-icon>
            Connection Timeline
          </v-card-title>
          <v-card-text style="max-height: 400px; overflow-y: auto;">
            <div v-if="loadingHistory" class="text-center pa-5">
              <v-progress-circular indeterminate color="primary" size="48" />
              <div class="mt-3">Loading connection history...</div>
            </div>

            <div v-else-if="filteredHistory.length === 0" class="text-center pa-5">
              <v-icon size="64" color="grey">history_toggle_off</v-icon>
              <div class="text-h6 mt-3 grey--text">No events found</div>
            </div>

            <v-timeline v-else dense class="timeline-custom">
              <v-timeline-item
                v-for="event in filteredHistory"
                :key="event.id"
                :color="getEventColor(event.event)"
                :icon="getEventIcon(event.event)"
                fill-dot
                small
              >
                <v-card :class="['elevation-2', 'event-card', `event-${event.event}`]">
                  <v-card-text class="pa-3">
                    <v-row dense align="center">
                      <v-col cols="12" sm="8">
                        <div class="d-flex align-center">
                          <v-chip
                            small
                            :color="getEventColor(event.event)"
                            dark
                            class="mr-2"
                          >
                            {{ getEventLabel(event.event) }}
                          </v-chip>
                          <div>
                            <div class="text-body-2 font-weight-medium">
                              {{ formatEventTime(event.timestamp) }}
                            </div>
                            <div class="text-caption text--secondary">
                              {{ formatEventDate(event.timestamp) }}
                            </div>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="4" class="text-sm-right">
                        <div v-if="event.duration_seconds" class="text-caption">
                          <v-icon small class="mr-1">timer</v-icon>
                          {{ formatDuration(event.duration_seconds) }}
                        </div>
                      </v-col>
                      <v-col cols="12" v-if="event.vpn_ip || event.public_ip">
                        <v-divider class="my-2" />
                        <div class="d-flex flex-wrap">
                          <div v-if="event.vpn_ip" class="mr-4 text-caption">
                            <v-icon small class="mr-1">vpn_key</v-icon>
                            VPN: {{ event.vpn_ip }}
                          </div>
                          <div v-if="event.public_ip" class="text-caption">
                            <v-icon small class="mr-1">public</v-icon>
                            Public: {{ event.public_ip }}
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          color="primary"
          outlined
          @click="exportCSV"
          :disabled="history.length === 0"
        >
          <v-icon left>download</v-icon>
          Export CSV
        </v-btn>
        <v-spacer />
        <v-btn
          color="grey"
          text
          @click="closeDialog"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import axios from 'axios'
import {mapState} from 'vuex'

export default {
  name: 'SensorHistory',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    sensorId: {
      type: String,
      default: ''
    },
    sensorName: {
      type: String,
      default: ''
    },
    areaName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: false,
      dateRange: 7,
      searchText: '',
      loadingHistory: false,
      loadingUptime: false,
      history: [],
      uptimeStats: null,
      dateRangeOptions: [
        {text: 'Last 7 days', value: 7},
        {text: 'Last 30 days', value: 30},
        {text: 'Last 90 days', value: 90}
      ]
    }
  },
  computed: {
    ...mapState({
      dateFormat: state => state.config.dateFormat
    }),
    filteredHistory() {
      if (!this.searchText)
        return this.history

      const searchLower = this.searchText.toLowerCase()
      return this.history.filter(event => event.event.toLowerCase().includes(searchLower)
          || (event.vpn_ip && event.vpn_ip.toLowerCase().includes(searchLower))
          || (event.public_ip && event.public_ip.toLowerCase().includes(searchLower)))
    },
    averageSessionDuration() {
      if (!this.uptimeStats || !this.uptimeStats.connection_events || this.uptimeStats.connection_events === 0)
        return 'N/A'

      const avgSeconds = this.uptimeStats.online_seconds / this.uptimeStats.connection_events
      return this.formatDuration(avgSeconds)
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal
      if (newVal && this.sensorId)
        this.loadData()
    },
    dialogVisible(newVal) {
      if (!newVal)
        this.$emit('close')
    },
    dateRange() {
      if (this.dialogVisible && this.sensorId)
        this.loadData()
    }
  },
  methods: {
    closeDialog() {
      this.dialogVisible = false
      this.$emit('close')
    },
    async loadData() {
      await Promise.all([
        this.loadHistory(),
        this.loadUptime()
      ])
    },
    async loadHistory() {
      if (!this.sensorId)
        return

      this.loadingHistory = true
      try {
        const response = await axios.get(`/api/sensors/${this.sensorId}/history`, {
          params: {limit: 100}
        })
        this.history = response.data.sort((a, b) => b.timestamp - a.timestamp)
      } catch (error) {
        console.error('Failed to load sensor history:', error)
        this.history = []
      } finally {
        this.loadingHistory = false
      }
    },
    async loadUptime() {
      if (!this.sensorId)
        return

      this.loadingUptime = true
      try {
        const response = await axios.get(`/api/sensors/${this.sensorId}/uptime`, {
          params: {days: this.dateRange}
        })
        this.uptimeStats = {
          uptime_percentage: response.data.uptime_percentage ? response.data.uptime_percentage.toFixed(1) : '0.0',
          online_seconds: response.data.online_seconds || 0,
          offline_seconds: response.data.offline_seconds || 0,
          connection_events: response.data.connection_events || 0
        }
      } catch (error) {
        console.error('Failed to load sensor uptime:', error)
        this.uptimeStats = null
      } finally {
        this.loadingUptime = false
      }
    },
    formatEventTime(timestamp) {
      return moment(timestamp * 1000).format('HH:mm:ss')
    },
    formatEventDate(timestamp) {
      return moment(timestamp * 1000).format('MMM DD, YYYY')
    },
    formatDuration(seconds) {
      if (!seconds || seconds === 0)
        return '0s'

      const duration = moment.duration(seconds, 'seconds')
      const days = Math.floor(duration.asDays())
      const hours = duration.hours()
      const minutes = duration.minutes()
      const secs = duration.seconds()

      const parts = []
      if (days > 0)
        parts.push(`${days}d`)
      if (hours > 0)
        parts.push(`${hours}h`)
      if (minutes > 0)
        parts.push(`${minutes}m`)
      if (secs > 0 || parts.length === 0)
        parts.push(`${secs}s`)

      return parts.join(' ')
    },
    getEventIcon(event) {
      const map = new Map([
        ['online', 'check_circle'],
        ['offline', 'cancel'],
        ['reconnect', 'sync']
      ])
      return map.get(event) || 'help'
    },
    getEventColor(event) {
      const map = new Map([
        ['connect', 'light-blue'],
        ['online', 'light-blue'],
        ['disconnect', 'grey'],
        ['offline', 'grey'],
        ['reconnect', 'info']
      ])
      return map.get(event) || 'grey'
    },
    getEventLabel(event) {
      const map = new Map([
        ['connect', 'Connected'],
        ['online', 'Online'],
        ['disconnect', 'Disconnected'],
        ['offline', 'Offline'],
        ['reconnect', 'Reconnected']
      ])
      return map.get(event) || event
    },
    getUptimeColor(uptime) {
      const value = parseFloat(uptime || 0)
      if (value >= 95)
        return 'success'
      if (value >= 80)
        return 'warning'
      return 'error'
    },
    exportCSV() {
      if (this.history.length === 0)
        return

      const headers = ['Event', 'Timestamp', 'Date', 'VPN IP', 'Public IP', 'Duration (seconds)']
      const rows = this.history.map(event => [
        event.event,
        event.timestamp,
        moment(event.timestamp * 1000).format('YYYY-MM-DD HH:mm:ss'),
        event.vpn_ip || '',
        event.public_ip || '',
        event.duration_seconds || ''
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `${this.sensorName}_history_${moment().format('YYYY-MM-DD')}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$emit('export')
    }
  }
}
</script>

<style scoped>
.sensor-history-dialog {
  border-radius: 16px !important;
}

.glass-stats {
  background: rgba(33, 126, 226, 0.05) !important;
}

.theme--dark .glass-stats {
  background: rgba(33, 126, 226, 0.1) !important;
}

.event-card {
  transition: all 0.2s ease;
}

.event-card:hover {
  transform: translateX(4px);
}

.event-connect {
  border-left: 4px solid #03A9F4 !important;
}

.event-online {
  border-left: 4px solid #03A9F4 !important;
}

.event-disconnect {
  border-left: 4px solid #9E9E9E !important;
}

.event-offline {
  border-left: 4px solid #9E9E9E !important;
}

.event-reconnect {
  border-left: 4px solid #4221a5 !important;
}

.timeline-custom {
  padding-top: 0 !important;
}

.v-timeline-item {
  padding-bottom: 16px !important;
}

.v-card.outlined {
  border-radius: 12px !important;
}
</style>
